import { useCreateCommunityGroup } from '@communities/community-groups/hooks';
import type { DomainError } from '@core/domain/errors';
import { track } from '@core/logging/appInsightsIntegration';
import { LoadingOverlay, TextInput, TextArea, Dialog } from '@soteria/design-system-ve';
import { useCallback, useEffect, useState } from 'react';

interface AddCommunityGroupDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Dialog for creating a new community group
 */
export function AddCommunityGroupDialog({ isOpen, onClose }: AddCommunityGroupDialogProps) {
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<DomainError | null>(null);

  const [currentStep, setCurrentStep] = useState<'creating' | 'success' | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setCurrentStep(null);
      setError(null); // Clear any previous errors
    }
  }, [isOpen]);

  // Form validation
  const isFormValid = name.trim() !== '' && description.trim() !== '';
  // Use the community groups hook
  const { createCommunityGroup, loading } = useCreateCommunityGroup();

  // Handle successful group creation
  const handleSuccess = useCallback(
    (groupName: string) => {
      // Track successful creation
      track('community_group_creation_success', {
        groupName,
      });

      // Close immediately on successful creation
      setCurrentStep(null);
      onClose(); // Close the dialog immediately
    },
    [onClose]
  );

  // Handle group creation error
  const handleError = useCallback((errorMessage: string, groupName: string) => {
    track('community_group_creation_failed', {
      groupName,
      error: errorMessage,
    });

    setCurrentStep(null);
  }, []);

  // Submit form handler
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!isFormValid) {
        return;
      }

      try {
        setCurrentStep('creating');
        setError(null); // Clear any previous errors

        const result = await createCommunityGroup({ name, description });

        if (result) {
          handleSuccess(name);
        }
      } catch (err) {
        const domainError = err as DomainError;

        setError(domainError);
        handleError(domainError.userMessage || 'Failed to create community group', name);
      }
    },
    [name, description, createCommunityGroup, isFormValid, handleSuccess, handleError]
  );

  // Helper function to get the loading message based on current step
  const getLoadingMessage = (step: 'creating' | 'success' | null): string => {
    switch (step) {
      case 'creating':
        return 'Creating community group...';
      case 'success':
        return 'Successfully created community group!';
      default:
        return 'Creating community group...';
    }
  };

  // Check if we're in a loading state
  const isSubmitting = currentStep !== null;

  if (isSubmitting) {
    return <LoadingOverlay fullScreen message={getLoadingMessage(currentStep)} spinnerSize="large" />;
  }

  return (
    <Dialog
      description="Create a new community group"
      isOpen={isOpen}
      primaryAction={{
        label: 'Create Community Group',
        onClick: () => {
          // Use a simple mock event object instead of trying to create a real event
          const mockEvent = {
            preventDefault: () => {
              /* no-op */
            },
          };

          void handleSubmit(mockEvent as React.FormEvent);
        },
        disabled: loading || !isFormValid,
      }}
      size="lg"
      title="Create Community Group"
      onClose={onClose}
    >
      {error && <div style={{ color: 'var(--color-error)', marginBottom: '16px' }}>{error.userMessage}</div>}

      {/* Name Input*/}
      <div style={{ marginBottom: '16px' }}>
        <TextInput required id="name" label="Name" placeholder="Enter community group name" value={name} onChange={(value: string) => setName(value)} />
      </div>

      {/* Description Input */}
      <div>
        <TextArea
          required
          id="description"
          placeholder="Enter community group description"
          rows={4}
          title="Description"
          value={description}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement> | string) => {
            // Handle both string value (from component) and event object (from native textarea)
            if (typeof e === 'string') {
              setDescription(e);
            } else if (e && typeof e === 'object' && 'target' in e) {
              setDescription(e.target.value);
            }
          }}
        />
      </div>
    </Dialog>
  );
}

export default AddCommunityGroupDialog;
